import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@repo/auth";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenueSubscription } from "../features/billing/queries";
import { useCurrentTrial } from "../features/billing/trialApi";
import { useVenue } from "./VenueContext";

// Same token-based vocabulary as SetupBanner (NOT DemoBanner's hard-coded hex), so the shell banners read
// as one system.
const banner = css({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "3",
  px: { base: "4", md: "6" },
  py: "2.5",
  fontSize: "sm",
  colorPalette: "brand",
  color: "colorPalette.text",
  bg: "colorPalette.subtle",
  borderBottomWidth: "1px",
  borderColor: "colorPalette.muted",
});
const tag = css({
  fontWeight: "bold",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  fontSize: "2xs",
  borderWidth: "1px",
  borderColor: "colorPalette.default",
  rounded: "sm",
  px: "1.5",
  py: "0.5",
});
const note = css({ flex: "1", minW: "16rem", color: "fg.default" });

/** The trial end date / a period boundary, localised in the venue's own time zone (e.g. "15 septembre 2026"). */
function formatDate(epochSec: number, lang: string, timeZone: string): string {
  return new Intl.DateTimeFormat(lang, { dateStyle: "long", timeZone }).format(
    new Date(epochSec * 1000),
  );
}

/**
 * The account-root UPGRADE banner — a persistent, product-wide nudge to subscribe whenever the venue is
 * NOT on a paid plan, independent of the onboarding `SetupBanner`. On an active free trial it shows the
 * trial END DATE; otherwise (no plan / cancelled / dunning) it invites the owner to pick a plan. It
 * disappears on its own once `subscription.state === "ACTIVE"`. Billing state is account-root-only (403
 * for managers/waiters), and only the owner can subscribe, so it renders for the account root alone.
 */
export function UpgradeBanner() {
  const { venueId, venue } = useVenue();
  const { claims } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isAccountRoot = claims?.isAccountRoot === true;
  const subscription = useVenueSubscription(venueId, isAccountRoot);
  const trial = useCurrentTrial(isAccountRoot);

  if (!isAccountRoot) return null;
  // The /billing page already shows the plan picker + its own trial/dunning banners — don't double up.
  if (pathname.endsWith("/billing")) return null;
  if (subscription.isLoading) return null;

  const state = subscription.data?.state;
  if (state === "ACTIVE") return null; // on a paid plan → nothing to nudge.

  // A trial is "active" per the (authoritative) trial endpoint; its endsAtSec is the real trial expiry
  // (the subscription DTO's currentPeriodEndSec is the Stripe cycle, not the semantic trial end).
  const trialEndsAtSec = trial.data?.state === "ACTIVE" ? trial.data.endsAtSec : null;
  const isTrial = trialEndsAtSec !== null;

  const tagText = isTrial ? t("upgrade.tag.trial") : t("upgrade.tag.plan");
  const message = isTrial
    ? t("upgrade.trial.message", {
        date: formatDate(trialEndsAtSec, i18n.language, venue.timeZone),
      })
    : t("upgrade.noPlan.message");

  return (
    <section
      aria-label={tagText}
      aria-live="polite"
      className={banner}
      data-testid="upgrade-banner"
      data-variant={isTrial ? "trial" : "plan"}
    >
      <span className={tag}>{tagText}</span>
      <span className={note}>{message}</span>
      <Button size="sm" variant="solid" onClick={() => navigate(`/v/${venueId}/billing`)}>
        {t("entitlement.upgrade.cta")}
      </Button>
    </section>
  );
}
